import {
  Image,
  PROFILE_PLACEHOLDER,
  openInNewTab,
} from "@apptractive/stm-shared";
import { Box, Skeleton } from "@mui/material";
import { useMemo } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  WBButton,
  WBFlex,
  WBTypography,
  WBS3AvatarUpload,
  WBS3Avatar,
  WBIconButton,
  WBChipList,
} from "@apptractive/design-system-web";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { UserType } from "../../API";
import { PageContainer } from "../../components/PageContainer";
import { getUser as GET_USER } from "../../graphql/queries";
import {
  CSGetSub as GET_SUB,
  CSgetUserOther as GET_USER_OTHER,
} from "../../customGraphql/queries";
import {
  //stripeCreateCustomerPortal as CREATE_STRIPE_PORTAL,
  updateUser as UPDATE_USER,
} from "../../graphql/mutations";
import { PATHS } from "../../navigation/paths";
import { Link } from "../../components/Link";

const User = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { data: subData } = useQuery(gql(GET_SUB));
  const { data: userData, loading: userLoading } = useQuery(gql(GET_USER), {
    variables: {
      id: userId,
    },
    skip: !subData || subData?.sub !== userId,
  });
  const { data: userOtherData, loading: userOtherLoading } = useQuery(
    gql(GET_USER_OTHER),
    {
      variables: {
        id: userId,
      },
      skip: !subData || subData?.sub === userId,
    }
  );
  const user = useMemo(
    () => userData?.getUser || userOtherData?.getUser || {},
    [userData, userOtherData]
  );
  const isUser = useMemo(() => subData?.sub === userId, [subData?.sub, userId]);
  const billing = useMemo(() => (isUser ? user?.billing : {}), [user, isUser]);

  //const { data: subscriptionData } = useQuery(
  //  gql(GET_SUBSCRIPTION),
  //  {
  //    variables: {
  //      id: billing?.subscriptionId,
  //    },
  //    skip: !billing || !billing?.subscriptionId,
  //  }
  //);

  //const subscription = useMemo(
  //  () => subscriptionData?.stripeGetSubscription || {},
  //  [subscriptionData]
  //);

  const [updateUser] = useMutation(gql(UPDATE_USER));
  //const [
  //  stripeCreateCustomerPortal,
  //  { data: createData, loading: createLoading },
  //] = useMutation(gql(CREATE_STRIPE_PORTAL));
  //
  //useEffect(() => {
  //  if (createData?.stripeCreateCustomerPortal?.url) {
  //    window.location.replace(createData.stripeCreateCustomerPortal.url);
  //  }
  //}, [createData]);

  const onImageUpload = async (image: Image) => {
    try {
      await updateUser({
        variables: { input: { id: userId, profileImg: image } },
      });
    } catch (err) {
      console.log("ERROR updating user: ", err);
    }
  };

  //const onCreateCustomerPortal = async () => {
  //  try {
  //    await stripeCreateCustomerPortal({
  //      variables: {
  //        input: {
  //          returnUrl: window.location.href,
  //        },
  //      },
  //    });
  //  } catch (err) {
  //    console.log("ERROR create customer portal: ", err);
  //  }
  //};

  const onPlanClick = () => {
    //billing?.productId ? onCreateCustomerPortal() : navigate(PATHS.plans);
    navigate(PATHS.plans);
  };

  return (
    <PageContainer>
      {isUser && (
        <WBFlex>
          <Box flex={1} />
          <Box flex={1} textAlign="right">
            <WBButton
              size="small"
              variant="outlined"
              onClick={() => navigate(`${PATHS.userUpdate}/${userId}`)}
            >
              {t("editProfile", { ns: "user" })}
            </WBButton>
          </Box>
        </WBFlex>
      )}
      <WBFlex flexDirection="column" alignItems="center">
        {isUser && (
          <WBS3AvatarUpload
            onImageUpload={onImageUpload}
            imgKey={user?.profileImg?.key}
            src={PROFILE_PLACEHOLDER.default}
            sx={{
              width: "120px",
              height: "120px",
            }}
          />
        )}

        {!isUser && (
          <WBS3Avatar
            imgKey={user?.profileImg?.key}
            src={PROFILE_PLACEHOLDER.default}
            sx={{
              width: "120px",
              height: "120px",
            }}
          />
        )}

        <WBTypography variant="h1" textAlign="center">
          {userLoading || userOtherLoading ? (
            <Skeleton width={200} />
          ) : (
            `${user?.firstName} ${user?.lastName}`
          )}
        </WBTypography>

        {user?.team?.title && (
          <WBTypography variant="h4">{user?.team?.title}</WBTypography>
        )}

        <WBFlex
          flexWrap="wrap"
          mt={1}
          justifyContent="center"
          alignItems="center"
        >
          <WBChipList
            tags={user?.interests || []}
            numTags={5}
            color="secondary"
            variant="filled"
            sx={{
              background: "linear-gradient(135deg, #63b4f6, #78E9D3)",
              color: "#2C4052",
            }}
          />
        </WBFlex>

        {isUser &&
          !userLoading &&
          !userOtherLoading &&
          user?.userType === UserType.Buyers && (
            <Link to={`${PATHS.tags}?category=${user.country}`} sx={{ mt: 2 }}>
              {t("addInterests", { ns: "user" })}
            </Link>
          )}

        {user?.about && (
          <WBTypography mt={2} textAlign="center">
            {user.about}
          </WBTypography>
        )}

        {isUser && !userLoading && !userOtherLoading && !user?.about && (
          <Link to={`${PATHS.userUpdate}/${subData?.sub}`} sx={{ mt: 2 }}>
            {t("addBio", { ns: "user" })}
          </Link>
        )}

        <WBFlex mt={1}>
          {user?.facebook && (
            <WBIconButton
              icon="LogoFacebook"
              library="ioniconSolid"
              onClick={() => openInNewTab(user.facebook)}
            />
          )}
          {user?.instagram && (
            <WBIconButton
              icon="LogoInstagram"
              library="ioniconSolid"
              onClick={() => openInNewTab(user.instagram)}
            />
          )}
          {user?.linkedin && (
            <WBIconButton
              icon="LogoLinkedin"
              library="ioniconSolid"
              onClick={() => openInNewTab(user.linkedin)}
            />
          )}
          {user?.twitter && (
            <WBIconButton
              icon="LogoTwitter"
              library="ioniconSolid"
              onClick={() => openInNewTab(user.twitter)}
            />
          )}
        </WBFlex>

        {isUser && (
          <WBButton
            sx={{ mt: 3 }}
            onClick={onPlanClick}
            //disabled={createLoading}
          >
            {!userLoading && billing?.productId
              ? t("viewPlan", { ns: "purchase" })
              : t("selectAPlan", { ns: "purchase" })}
          </WBButton>
        )}

        {isUser && user?.teamId && (
          <WBButton
            onClick={() => navigate(`${PATHS.team}/${user?.teamId}`)}
            sx={{ mt: 3 }}
          >
            {t("viewTeamTitle", { ns: "team" })}
          </WBButton>
        )}
        {isUser &&
          !user?.teamId &&
          billing?.productId &&
          user?.userType === UserType.Sellers && (
            <WBButton
              onClick={() => navigate(`${PATHS.teamCreate}`)}
              sx={{ mt: 3 }}
            >
              {t("createTeamTitle", { ns: "team" })}
            </WBButton>
          )}
      </WBFlex>
    </PageContainer>
  );
};

export default User;
